import React, { useEffect, useMemo } from "react";
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ListRenderItem,
  Pressable,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import { fetchReturns } from "@actions/returns.actions";
import t from "@constants/localization";
import BackArrow from "../../assets/svg/BackArrow";
import ChevronRight from "../../assets/svg/ChevronRight";
import color from "../../constants/color";
import {
  selectLoading,
  selectReturnsData,
} from "../../selectors/returns.selector";
import { CardItem, ReturnsData } from "./types";
import { sectionsOrder } from "./constants";

const PATH = "screens/Returns";

interface Returns {
  route: {
    params: {
      date: string;
      spokeName: string;
    };
  };
}

function Returns({ route }: Returns) {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const returnsData: ReturnsData = useSelector((state: any) =>
    selectReturnsData(state)
  );
  const loading = useSelector((state: any) => selectLoading(state));
  const { features = {} } = useSelector(
    (state: any) => state.configReducer
  );

  useEffect(() => {
    fetchReturns({
      date: route?.params?.date,
      spokeName: route?.params?.spokeName,
    })(dispatch);
  }, [dispatch]);

  const filteredSections = useMemo(() => {
    return Object.entries(returnsData?.returnStats || {})
      .sort(([sectionA], [sectionB]) => {
        return (
          sectionsOrder.indexOf(sectionA) - sectionsOrder.indexOf(sectionB)
        );
      })
      .map(([_, value]) => ({ id: _, ...value }));
  }, [returnsData.returnStats]);

  const navigateToCrateOrItemDetails = (item: CardItem) => {
    if (["CRATES", "PACKEDCRATES"].includes(item?.id?.split("_")[0])) {
      if (item?.navigation === "BLOCKED") {
        return;
      } else {
        navigation.navigate("ReturnCrateDetails", {
          navKey: item?.id?.split("_")[1],
          crateType: item?.id?.split("_")[0],
        })
      }
    } else {
      navigation.navigate("ReturnItemDetails", {
        navKey: item?.id?.split("_")[1],
      })
    }
  }

  const renderCard: ListRenderItem<CardItem> = ({ item }) => (
    <Pressable
      style={styles.card}
      android_ripple={{
        color: color.shadow,
        borderless: false,
      }}
      onPress={
        item.quantity > 0 ? () => navigateToCrateOrItemDetails(item) : () => {}
      }
    >
      <Text style={styles.cardTitle}>{t(`${PATH}/${item.id}`)}</Text>
      <View style={styles.cardRight}>
        <View style={styles.refundAmountContainer}>
          <Text style={styles.qty}>
            {t(`${PATH}/qty`, { qty: item.quantity })}
          </Text>
          {features?.return_amount && item.refundAmount ? (
            <>
              <Text
                style={{
                  color: color.lighterBlack,
                  marginHorizontal: 8,
                }}
              >
                |
              </Text>
              <View>
                <Text style={styles.refundAmount}>₹{item.refundAmount}</Text>
                <Text style={styles.refundText}>{t(`${PATH}/refund`)}</Text>
              </View>
            </>
          ) : null}
        </View>
        {item.quantity > 0 ? (
          <View style={styles.chevronContainer}>
            <ChevronRight pathFill={color.blue} style={styles.chevronIcon} />
          </View>
        ) : null}
      </View>
    </Pressable>
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <BackArrow />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t(`${PATH}/title`)}</Text>
      </View>
      {loading ? (
        <ActivityIndicator
          color={color.primary}
          size="large"
          style={styles.loader}
        />
      ) : filteredSections.length === 0 ? (
        <View style={styles.loader}>
          <Text>{t(`${PATH}/noReturns`)}</Text>
        </View>
      ) : (
        <FlatList
          data={filteredSections}
          keyExtractor={(item) => item.id}
          renderItem={renderCard}
          contentContainerStyle={styles.listContent}
          getItemLayout={(_, index) => ({
            length: 48,
            offset: 48 * index,
            index,
          })}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.darkBackground,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: color.white,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: "bold",
    marginLeft: 16,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  card: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    marginVertical: 8,
    backgroundColor: color.white,
    borderRadius: 8,
    height: 50,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: color.black,
  },
  cardRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  qty: {
    color: color.grey,
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 20,
  },
  chevronContainer: {
    width: 40,
    alignItems: "flex-end",
  },
  chevronIcon: {
    height: 12,
    width: 12,
  },
  loader: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  refundAmount: {
    fontSize: 14,
    color: color.black,
    fontWeight: "600",
  },
  refundText: {
    fontSize: 10,
    color: color.darkGrey,
    marginBottom: 4,
    fontWeight: "400",
    textAlign: "center",
  },
  refundAmountContainer: { flexDirection: "row" },
});

export default Returns;
